<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showProviderRate") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #1b706f">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: User Data Section -->
          <div class="col-12 mb-4">
            <h5 class="section_title">{{ $t("TITLES.userData") }}</h5>
          </div>

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.client-name')"
            v-model.trim="data.user_name"
            disabled
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.user_mobile"
            disabled
          />
          <!-- End:: User Data Section -->

          <!-- Start:: Provider Data Section -->
          <div class="col-12 mb-4 mt-4">
            <h5 class="section_title">{{ $t("TITLES.providerData") }}</h5>
          </div>

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.provider_name')"
            v-model.trim="data.provider_name"
            disabled
          />
          <!-- End:: Provider Data Section -->

          <!-- Start:: Rating Data Section -->
          <div class="col-12 mb-4 mt-4">
            <h5 class="section_title">{{ $t("TITLES.ratingData") }}</h5>
          </div>

          <base-rate-input
            col="6"
            :placeholder="$t('PLACEHOLDERS.rating_stars')"
            v-model.trim="data.rate"
            size="22"
            :readonly="true"
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.created_at')"
            v-model.trim="data.created_at"
            disabled
          />

          <base-input
            v-if="data.message"
            col="12"
            type="textarea"
            :placeholder="$t('TABLES.ProviderRates.comment')"
            v-model.trim="data.message"
            disabled
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.orderNumber')"
            v-model.trim="data.order_id"
            disabled
          />
          <!-- End:: Rating Data Section -->

          <!-- Start:: Action Buttons -->
          <div class="col-12 mt-4">
            <div class="action_buttons_wrapper">
              <button
                v-if="!data.is_published"
                class="success_btn"
                @click="publishRating"
                :disabled="isWaitingRequest"
              >
                <i class="fal fa-check-circle"></i>
                {{ $t("BUTTONS.publishRating") }}
              </button>

              <button
                class="danger_btn"
                @click="showDeleteDialog"
                :disabled="isWaitingRequest"
              >
                <i class="fal fa-trash-alt"></i>
                {{ $t("BUTTONS.delete") }}
              </button>
            </div>
          </div>
          <!-- End:: Action Buttons -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->

    <!-- Start:: Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDelete" max-width="500">
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.DeleteProviderRate") }}
        </v-card-title>
        <v-card-text class="text-center">
          {{ $t("MESSAGES.deleteRatingConfirmation") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="modal_confirm_btn"
            @click="confirmDeleteRating"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.yes") }}
          </v-btn>
          <v-btn
            class="modal_cancel_btn"
            @click="dialogDelete = false"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Delete Confirmation Dialog -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ShowProviderRate",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Dialog Control Data
      dialogDelete: false,
      // End:: Dialog Control Data

      // Start:: Data Collection To Send
      data: {
        user_name: null,
        user_mobile: null,
        provider_name: null,
        rate: null,
        message: null,
        created_at: null,
        order_serial: null,
        order_id: null,
        is_published: false,
      },
      // End:: Data Collection To Send
    };
  },

  methods: {
    // Start:: Show Rate Data
    async showRate() {
      this.isWaitingRequest = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `providerRates/${this.$route.params?.id}`,
        });

        // Handle new response structure: res.data.data.ProviderRate
        const rateData =
          res.data.data?.ProviderRate ||
          res.data.data?.data ||
          res.data.data ||
          res.data;

        this.data.user_name = rateData.user?.name || "-";
        this.data.user_mobile = rateData.user?.mobile || "-";
        this.data.provider_name = rateData.provider?.name || "-";
        this.data.rate = rateData?.rate;
        this.data.message = rateData?.message || null;
        this.data.order_id = rateData?.order_id;
        this.data.created_at = rateData.created_at;
        this.data.order_serial = rateData.order?.order_id || "-";
        this.data.is_published = rateData?.publish;

        this.isWaitingRequest = false;
      } catch (error) {
        this.isWaitingRequest = false;
        console.log(error?.response?.data?.message);
        this.$message.error(
          error?.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Show Rate Data

    // Start:: Validate Form Inputs
    validateFormInputs() {
      // This form is read-only, no validation needed
    },
    // End:: Validate Form Inputs

    // Start:: Publish Rating
    async publishRating() {
      this.isWaitingRequest = true;
      try {
        await this.$axios({
          method: "POST",
          url: `providerRates/publish/${this.$route.params?.id}`,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.ratingPublishedSuccessfully"));
        this.data.publish = true;
        // Optionally redirect back to list
        // this.$router.push({ path: "/provider-rates/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error?.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Publish Rating

    // Start:: Delete Rating
    showDeleteDialog() {
      this.dialogDelete = true;
    },
    async confirmDeleteRating() {
      this.isWaitingRequest = true;
      try {
        await this.$axios({
          method: "DELETE",
          url: `providerRates/${this.$route.params?.id}`,
        });
        this.dialogDelete = false;
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        // Redirect back to list
        this.$router.push({ path: "/provider-rates/all" });
      } catch (error) {
        this.dialogDelete = false;
        this.isWaitingRequest = false;
        this.$message.error(
          error?.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Delete Rating
  },

  created() {
    this.showRate();
  },
};
</script>

<style scoped>
.section_title {
  color: var(--main_theme_clr);
  font-weight: bold;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--main_theme_clr);
}

.action_buttons_wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.success_btn {
  background-color: #28a745 !important;
  color: white !important;
  padding: 10px 20px;
  font-weight: bold;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.success_btn:hover:not(:disabled) {
  background-color: #218838 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
}

.danger_btn {
  background-color: #dc3545 !important;
  color: white !important;
  padding: 10px 20px;
  font-weight: bold;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.danger_btn:hover:not(:disabled) {
  background-color: #c82333 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.success_btn:disabled,
.danger_btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
